import { useEffect, useState } from "react";
import { Archive, Info, X } from "lucide-react";
import { ARCHIVE_BANNER_HEADLINE, ARCHIVE_BANNER_SUBLINE, ARCHIVE_DATE, ARCHIVE_MODE } from "@/config/archive";
import { ArchiveNoticeDialog } from "./ArchiveNoticeDialog";

const DISMISS_KEY = "rr:archive-banner-dismissed";
const SEEN_KEY = "rr:archive-notice-seen";

/**
 * High-visibility archive banner pinned above the navigation on every page.
 *
 * Visibility rules:
 *  • The full notice dialog auto-opens once per browser (first visit) so no
 *    one can miss that the platform is frozen.
 *  • The bar itself is a solid primary band — not a subtle tint — with a
 *    thermal gradient edge and a live status dot.
 *  • Dismissing collapses it to a persistent chip instead of hiding it; the
 *    notice always stays one click away.
 */
export const ArchiveBanner = () => {
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [open, setOpen] = useState(false);

  // First visit in this browser: surface the full notice automatically.
  useEffect(() => {
    if (!ARCHIVE_MODE || typeof window === "undefined") return;
    try {
      if (window.localStorage.getItem(SEEN_KEY) === "1") return;
      window.localStorage.setItem(SEEN_KEY, "1");
    } catch {
      return;
    }
    const timer = window.setTimeout(() => setOpen(true), 700);
    return () => window.clearTimeout(timer);
  }, []);

  if (!ARCHIVE_MODE) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* storage unavailable — banner simply reappears next render */
    }
  };

  return (
    <>
      {dismissed ? (
        <div className="relative z-50 flex justify-center border-b border-border/60 bg-background/80 px-4 py-1.5 backdrop-blur-md">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/20"
          >
            <Archive className="h-3 w-3" />
            Archived snapshot — read the notice
          </button>
        </div>
      ) : (
        <div className="relative z-50 bg-primary text-primary-foreground shadow-lg">
          <div aria-hidden className="h-1 w-full bg-gradient-thermal" />
          <div className="container mx-auto flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
            <span className="inline-flex w-fit shrink-0 items-center gap-2 rounded-full bg-primary-foreground/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em]">
              <span aria-hidden className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-foreground/70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-foreground" />
              </span>
              Archived {ARCHIVE_DATE}
            </span>
            <p className="min-w-0 flex-1 text-sm leading-snug">
              <span className="font-bold">{ARCHIVE_BANNER_HEADLINE}.</span>{" "}
              <span className="text-primary-foreground/85">{ARCHIVE_BANNER_SUBLINE}</span>
            </p>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary-foreground px-3.5 py-1.5 text-xs font-bold text-primary shadow-sm transition-transform hover:scale-[1.03]"
              >
                <Info className="h-3.5 w-3.5" />
                What this means
              </button>
              <button
                type="button"
                onClick={dismiss}
                aria-label="Collapse archive notice"
                className="rounded-full p-1 text-primary-foreground/70 transition-colors hover:bg-primary-foreground/15 hover:text-primary-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      <ArchiveNoticeDialog open={open} onOpenChange={setOpen} />
    </>
  );
};

export default ArchiveBanner;
